import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  CreditCard,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  Check,
  Calendar,
  MapPin,
  FileText,
  AlertCircle,
  Share2,
  Download,
  Edit3,
  Clock,
  Star,
  Printer,
  RotateCw,
  Shield,
  Trash2,
} from 'lucide-react';
import { detectCardNetwork } from './CardEditorModal';

export default function CardViewModal({
  open,
  card,
  onClose,
  onEdit,
  onToggleFavorite,
  onDelete,
  onShare,
  hasEditPermission = true,
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showFullNumber, setShowFullNumber] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // PIN gate protection
  const [pinEntered, setPinEntered] = useState('');
  const [isPinUnlocked, setIsPinUnlocked] = useState(false);
  const [pinError, setPinError] = useState('');

  // Copied indicator
  const [copiedField, setCopiedField] = useState(null);
  const [clipboardNotice, setClipboardNotice] = useState('');

  const autoHideTimerRef = useRef(null);
  const noticeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setIsFlipped(false);
      setShowFullNumber(false);
      setShowCvv(false);
      setShowPin(false);
      setIsPinUnlocked(false);
      setPinEntered('');
      setPinError('');
      setClipboardNotice('');
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    }
  }, [open]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !card) return null;

  const f = card.fields || card;
  const addr = f.billingAddress || {};
  const network = detectCardNetwork(f.cardNumber);
  const theme = f.theme || card.theme || 'sapphire';

  // Expiry calculation
  const getExpiryStatus = () => {
    if (!f.expiryMonth || !f.expiryYear) return null;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const expY = parseInt(f.expiryYear, 10);
    const expM = parseInt(f.expiryMonth, 10);

    if (isNaN(expY) || isNaN(expM)) return null;

    if (expY < currentYear || (expY === currentYear && expM < currentMonth)) {
      return { status: 'expired', label: 'Expired', color: '#ef4444' };
    }

    // Expiring soon if this month or next month (within 30-60 days)
    if (expY === currentYear && expM === currentMonth) {
      return { status: 'expiring_soon', label: 'Expires this month!', color: '#f59e0b' };
    }
    if (expY === currentYear && expM === currentMonth + 1) {
      return { status: 'expiring_soon', label: 'Expiring next month', color: '#f59e0b' };
    }

    return { status: 'valid', label: 'Active', color: '#10b981' };
  };

  const expiryBadge = getExpiryStatus();

  // Reveal toggle with 15s auto-hide
  const toggleRevealSensitives = () => {
    if (card.isLocked && !isPinUnlocked) {
      return;
    }

    const nextState = !showFullNumber;
    setShowFullNumber(nextState);
    setShowCvv(nextState);
    setShowPin(nextState);

    if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    if (nextState) {
      autoHideTimerRef.current = setTimeout(() => {
        setShowFullNumber(false);
        setShowCvv(false);
        setShowPin(false);
      }, 15000);
    }
  };

  const handleUnlockPin = (e) => {
    e.preventDefault();
    if (pinEntered === card.lockPin) {
      setIsPinUnlocked(true);
      setPinError('');
      setShowFullNumber(true);
      setShowCvv(true);
      setShowPin(true);
      autoHideTimerRef.current = setTimeout(() => {
        setShowFullNumber(false);
        setShowCvv(false);
        setShowPin(false);
      }, 15000);
    } else {
      setPinError('Incorrect PIN. Please try again.');
    }
  };

  const copyToClipboard = (text, fieldName, isSensitive = false) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);

    if (isSensitive) {
      setClipboardNotice('Copied! Sensitive payment data will clear from clipboard shortly.');
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = setTimeout(() => setClipboardNotice(''), 4000);
    }
  };

  // Masked string representations
  const getMaskedCardNumber = (raw) => {
    if (!raw) return '•••• •••• •••• ••••';
    const clean = raw.replace(/\D/g, '');
    const last4 = clean.slice(-4) || '••••';
    return `•••• •••• •••• ${last4}`;
  };

  const fullAddress = [addr.street, addr.city, addr.state || addr.stateProvince, addr.postalCode, addr.country]
    .filter(Boolean)
    .join(', ');

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="card-drawer-wrapper">
      <div
        className="card-drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="card-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="View Payment Card"
      >
        {/* Drawer Header */}
        <div className="card-drawer-header">
          <div className="card-drawer-header-left">
            <div
              className="card-drawer-icon-badge"
              style={{ background: 'rgba(59, 130, 246, 0.14)', color: '#3b82f6' }}
            >
              <CreditCard size={20} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">
                {card.cardName || card.title}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                <span className="badge badge-primary" style={{ fontSize: '0.66rem', padding: '1px 6px' }}>
                  {(card.cardType || 'Credit').toUpperCase()}
                </span>
                <span className="badge badge-secondary" style={{ fontSize: '0.66rem', padding: '1px 6px' }}>
                  {card.category || 'Finance'}
                </span>
                {expiryBadge && (
                  <span
                    className="identity-expiry-status-pill"
                    style={{
                      backgroundColor: `${expiryBadge.color}15`,
                      color: expiryBadge.color,
                      borderColor: `${expiryBadge.color}35`,
                      fontSize: '0.66rem',
                      padding: '1px 6px',
                    }}
                  >
                    <Clock size={10} />
                    {expiryBadge.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="card-drawer-header-actions">
            {onToggleFavorite && (
              <button
                type="button"
                className={`btn-icon btn-sm ${card.is_favorite ? 'text-warning' : ''}`}
                onClick={() => onToggleFavorite(card)}
                title={card.is_favorite ? 'Remove Favorite' : 'Add to Favorites'}
              >
                <Star size={16} fill={card.is_favorite ? 'currentColor' : 'none'} />
              </button>
            )}
            <button
              type="button"
              className="btn-icon btn-sm"
              onClick={handlePrint}
              title="Print Summary"
            >
              <Printer size={16} />
            </button>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Drawer Body */}
        <div className="card-drawer-body">
          {/* Clipboard Notice Alert */}
          {clipboardNotice && (
            <div className="alert alert-info animate-slide-down" style={{ padding: '8px 12px', fontSize: '0.78rem', marginBottom: '12px' }}>
              <Shield size={14} />
              <span>{clipboardNotice}</span>
            </div>
          )}

          {/* Interactive Digital Card (Front / Back Flip View) */}
          <div className="digital-card-interactive-wrapper">
            {!isFlipped ? (
              /* Card Front */
              <div className={`digital-card-interactive front theme-${theme}`}>
                <div className="card-interactive-top">
                  <span className="card-interactive-issuer">{f.issuer || 'PAYMENT CARD'}</span>
                  <div className="card-interactive-top-right">
                    <span className="card-interactive-network">{network.name}</span>
                    <button
                      type="button"
                      className="card-flip-btn"
                      onClick={() => setIsFlipped(true)}
                      title="Flip to back of card"
                    >
                      <RotateCw size={12} />
                      <span>Back</span>
                    </button>
                  </div>
                </div>

                <div className="card-interactive-chip-row">
                  <div className="card-hero-emv-chip" style={{ width: '36px', height: '26px' }}></div>
                  <div className="card-hero-contactless">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>

                <div className="card-interactive-number-row">
                  <div className="card-interactive-number">
                    {showFullNumber ? f.cardNumber || '—' : getMaskedCardNumber(f.cardNumber)}
                  </div>
                </div>

                <div className="card-interactive-bottom">
                  <div>
                    <span className="card-hero-sublabel" style={{ fontSize: '0.58rem' }}>CARDHOLDER NAME</span>
                    <span className="card-hero-holder-name" style={{ fontSize: '0.78rem' }}>
                      {f.cardholderName?.toUpperCase() || 'CARDHOLDER'}
                    </span>
                  </div>
                  <div>
                    <span className="card-hero-sublabel" style={{ fontSize: '0.58rem' }}>VALID THRU</span>
                    <span className="card-hero-exp-date" style={{ fontSize: '0.78rem' }}>
                      {f.expiryMonth || 'MM'}/{(f.expiryYear || 'YYYY').slice(-2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              /* Card Back */
              <div className={`digital-card-interactive back theme-${theme}`}>
                <div className="card-interactive-magstripe" style={{ height: '36px' }}></div>
                <div className="card-interactive-back-signature-row" style={{ padding: '0 20px' }}>
                  <div className="card-interactive-signature-strip" style={{ height: '30px', fontSize: '0.62rem' }}>
                    <span>AUTHORIZED SIGNATURE</span>
                  </div>
                  <div className="card-interactive-cvv-box" style={{ width: '50px', height: '30px' }}>
                    <span className="cvv-label" style={{ fontSize: '0.5rem' }}>CVV</span>
                    <span className="cvv-val" style={{ fontSize: '0.78rem' }}>{showCvv ? f.cvv || '—' : '•••'}</span>
                  </div>
                </div>
                <div className="card-interactive-back-bottom" style={{ padding: '0 20px' }}>
                  <p className="card-interactive-back-notes" style={{ fontSize: '0.62rem' }}>
                    Customer Service: 1-800-000-0000 • {f.issuer || 'Card Issuer'}
                  </p>
                  <button
                    type="button"
                    className="card-flip-btn"
                    onClick={() => setIsFlipped(false)}
                    title="Flip to front of card"
                  >
                    <RotateCw size={12} />
                    <span>Front</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sensitive Reveal Action & PIN Gate */}
          <div className="card-ios-group">
            <div className="card-ios-row" style={{ background: 'rgba(59, 130, 246, 0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={16} className="text-primary" />
                <span style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.04em' }}>
                  SECURE PAYMENT DATA
                </span>
              </div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                {showFullNumber ? 'Auto-hides in 15s' : 'Masked & Protected'}
              </span>
            </div>

            {card.isLocked && !isPinUnlocked ? (
              <form onSubmit={handleUnlockPin} style={{ padding: '14px', textAlign: 'center' }}>
                <Lock size={20} className="text-warning" style={{ margin: '0 auto 6px auto', display: 'block' }} />
                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 10px 0' }}>
                  This card is PIN protected. Enter PIN to reveal sensitive details.
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <input
                    type="password"
                    maxLength={8}
                    className="form-input"
                    style={{ maxWidth: '140px', padding: '6px 10px', fontSize: '0.85rem' }}
                    placeholder="Enter PIN"
                    value={pinEntered}
                    onChange={(e) => setPinEntered(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary btn-sm">
                    Unlock
                  </button>
                </div>
                {pinError && <span className="text-danger text-sm" style={{ display: 'block', marginTop: '6px' }}>{pinError}</span>}
              </form>
            ) : (
              <div style={{ padding: '10px 14px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: '0.76rem', padding: '5px 10px' }}
                  onClick={toggleRevealSensitives}
                >
                  {showFullNumber ? <EyeOff size={14} /> : <Eye size={14} />}
                  <span>{showFullNumber ? 'Hide Details' : 'Reveal Secure Details'}</span>
                </button>

                {f.cardNumber && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.76rem', padding: '5px 10px' }}
                    onClick={() => copyToClipboard(f.cardNumber.replace(/\s+/g, ''), 'cardNumber', true)}
                  >
                    {copiedField === 'cardNumber' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    <span>{copiedField === 'cardNumber' ? 'Copied' : 'Copy Number'}</span>
                  </button>
                )}

                {f.cvv && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.76rem', padding: '5px 10px' }}
                    onClick={() => copyToClipboard(f.cvv, 'cvv', true)}
                  >
                    {copiedField === 'cvv' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    <span>{copiedField === 'cvv' ? 'Copied' : 'Copy CVV'}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* iOS Inset Group: Card Details */}
          <div className="card-ios-group">
            <h4 className="card-ios-group-title">Card Credentials</h4>

            <div className="card-ios-row">
              <span className="card-ios-label">Cardholder</span>
              <div className="card-ios-value-wrap">
                <span className="card-ios-value">{f.cardholderName || '—'}</span>
                {f.cardholderName && (
                  <button
                    type="button"
                    className="card-ios-action-btn"
                    onClick={() => copyToClipboard(f.cardholderName, 'holder')}
                    title="Copy Cardholder Name"
                  >
                    {copiedField === 'holder' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                )}
              </div>
            </div>

            <div className="card-ios-row">
              <span className="card-ios-label">Card Number</span>
              <div className="card-ios-value-wrap">
                <span className="card-ios-value" style={{ fontFamily: 'monospace' }}>
                  {showFullNumber ? f.cardNumber || '—' : getMaskedCardNumber(f.cardNumber)}
                </span>
                {f.cardNumber && (
                  <button
                    type="button"
                    className="card-ios-action-btn"
                    onClick={() => copyToClipboard(f.cardNumber.replace(/\s+/g, ''), 'cardNumber', true)}
                    title="Copy Card Number"
                  >
                    {copiedField === 'cardNumber' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                )}
              </div>
            </div>

            <div className="card-ios-row">
              <span className="card-ios-label">Expires</span>
              <div className="card-ios-value-wrap">
                <span className="card-ios-value">
                  {f.expiryMonth || 'MM'}/{f.expiryYear || 'YYYY'}
                </span>
                {f.expiryMonth && f.expiryYear && (
                  <button
                    type="button"
                    className="card-ios-action-btn"
                    onClick={() => copyToClipboard(`${f.expiryMonth}/${f.expiryYear}`, 'expiry', true)}
                    title="Copy Expiry"
                  >
                    {copiedField === 'expiry' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                )}
              </div>
            </div>

            <div className="card-ios-row">
              <span className="card-ios-label">CVV / CVC</span>
              <div className="card-ios-value-wrap">
                <span className="card-ios-value" style={{ fontFamily: 'monospace' }}>
                  {showCvv ? f.cvv || '—' : '•••'}
                </span>
                {f.cvv && (
                  <button
                    type="button"
                    className="card-ios-action-btn"
                    onClick={() => copyToClipboard(f.cvv, 'cvv', true)}
                    title="Copy CVV"
                  >
                    {copiedField === 'cvv' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                )}
              </div>
            </div>

            {f.pin && (
              <div className="card-ios-row">
                <span className="card-ios-label">Card PIN</span>
                <div className="card-ios-value-wrap">
                  <span className="card-ios-value" style={{ fontFamily: 'monospace' }}>
                    {showPin ? f.pin : '••••'}
                  </span>
                  <button
                    type="button"
                    className="card-ios-action-btn"
                    onClick={() => copyToClipboard(f.pin, 'pin', true)}
                    title="Copy PIN"
                  >
                    {copiedField === 'pin' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            )}

            <div className="card-ios-row">
              <span className="card-ios-label">Bank / Issuer</span>
              <span className="card-ios-value">{f.issuer || '—'}</span>
            </div>

            <div className="card-ios-row">
              <span className="card-ios-label">Network</span>
              <span className="card-ios-value">{network.name}</span>
            </div>

            <div className="card-ios-row">
              <span className="card-ios-label">Currency</span>
              <span className="card-ios-value">{f.currency || 'USD'}</span>
            </div>
          </div>

          {/* Linked Bank Account (Optional) */}
          {(f.accountNumber || f.ifscCode) && (
            <div className="card-ios-group">
              <div style={{ padding: '10px 14px 4px' }}>
                <h4 className="card-ios-group-title" style={{ padding: 0 }}>Linked Bank Account</h4>
              </div>

              {f.accountNumber && (
                <div className="card-ios-row">
                  <span className="card-ios-label">Account Number</span>
                  <div className="card-ios-value-wrap">
                    <span className="card-ios-value" style={{ fontFamily: 'monospace' }}>
                      {showFullNumber ? f.accountNumber : `•••• •••• ${f.accountNumber.slice(-4) || '••••'}`}
                    </span>
                    <button
                      type="button"
                      className="card-ios-action-btn"
                      onClick={() => copyToClipboard(f.accountNumber, 'accountNumber', true)}
                      title="Copy Account Number"
                    >
                      {copiedField === 'accountNumber' ? (
                        <Check size={13} className="text-success" />
                      ) : (
                        <Copy size={13} />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {f.ifscCode && (
                <div className="card-ios-row">
                  <span className="card-ios-label">IFSC Code</span>
                  <div className="card-ios-value-wrap">
                    <span className="card-ios-value" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}>
                      {f.ifscCode}
                    </span>
                    <button
                      type="button"
                      className="card-ios-action-btn"
                      onClick={() => copyToClipboard(f.ifscCode, 'ifscCode')}
                      title="Copy IFSC Code"
                    >
                      {copiedField === 'ifscCode' ? (
                        <Check size={13} className="text-success" />
                      ) : (
                        <Copy size={13} />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Billing Address Inset Group */}
          {fullAddress && (
            <div className="card-ios-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 4px' }}>
                <h4 className="card-ios-group-title" style={{ padding: 0 }}>Billing Address</h4>
                <button
                  type="button"
                  className="card-ios-action-btn"
                  onClick={() => copyToClipboard(fullAddress, 'address')}
                  title="Copy Full Address"
                >
                  {copiedField === 'address' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                </button>
              </div>
              <div style={{ padding: '8px 14px 12px' }}>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: 1.4 }}>
                  {fullAddress}
                </p>
              </div>
            </div>
          )}

          {/* Attached Scans */}
          {Array.isArray(card.attachments) && card.attachments.length > 0 && (
            <div className="card-ios-group">
              <h4 className="card-ios-group-title">Card Scans ({card.attachments.length})</h4>
              <div style={{ padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {card.attachments.map((att) => {
                  const isImage = att.type?.startsWith('image/');
                  const sizeKb = (att.size / 1024).toFixed(1);
                  return (
                    <div
                      key={att.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--bg-surface)',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        {isImage && att.dataUrl ? (
                          <img src={att.dataUrl} alt={att.name} style={{ width: '32px', height: '24px', borderRadius: '4px', objectFit: 'cover' }} />
                        ) : (
                          <CreditCard size={20} className="text-primary" />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {att.name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{sizeKb} KB</div>
                        </div>
                      </div>
                      {att.dataUrl && (
                        <a
                          href={att.dataUrl}
                          download={att.name}
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: '0.72rem', padding: '4px 8px' }}
                        >
                          <Download size={12} />
                          <span>Save</span>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Confidential Notes */}
          {f.notes && (
            <div className="card-ios-group">
              <h4 className="card-ios-group-title">Confidential Notes</h4>
              <div style={{ padding: '8px 14px 12px' }}>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                  {f.notes}
                </p>
              </div>
            </div>
          )}

          {/* Tags */}
          {Array.isArray(card.tags) && card.tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
              {card.tags.map((t) => (
                <span key={t} className="tag-chip" style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Drawer Sticky Footer */}
        <div className="card-drawer-footer">
          <div>
            {onDelete && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                style={{ fontSize: '0.78rem', padding: '6px 12px' }}
                onClick={() => onDelete(card)}
              >
                <Trash2 size={13} />
                <span>Move to Trash</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ fontSize: '0.78rem', padding: '6px 12px' }}
              onClick={onClose}
            >
              Close
            </button>
            {hasEditPermission && onEdit && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ fontSize: '0.78rem', padding: '6px 14px' }}
                onClick={() => {
                  onClose();
                  onEdit(card);
                }}
              >
                <Edit3 size={13} />
                <span>Edit Card</span>
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
